import type {
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from 'mantine-react-table';
import React from 'react';
import type {ComplianceStandardResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetCustomComplianceStandardListV2Query} from 'src/redux/queries/complianceStandard';
import {mergeComplianceScope} from 'src/app/compliance-standards/complianceStandardTreeState';
import {useResetOnScopeChange} from 'src/app/tree-browser/useResetOnScopeChange';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {useCompanyStandardColumns} from './hooks/useCompanyStandardColumns';

export interface CompanyStandardTableProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  onViewRowClick: (row: ComplianceStandardResponse) => void;
  onCreate?: () => void;
  onImport?: () => void;
  scopeNodeId?: string;
}

export const CompanyStandardTable: React.FC<CompanyStandardTableProps> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
  onViewRowClick,
  onCreate,
  onImport,
  scopeNodeId,
}) => {
  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.Compliance,
    operation: PermissionOperationEnum.Create,
  });

  const {hasPermission: hasImportPermission} = useHasModulePermission({
    module: ModuleEnum.Compliance,
    operation: PermissionOperationEnum.Import,
  });

  const columns = useCompanyStandardColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'sectionLabel', desc: false}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;
  const scopedRequest = mergeComplianceScope(filters, filterFns, scopeNodeId);
  useResetOnScopeChange(scopeNodeId, () =>
    tableState.setPagination(current => ({...current, pageIndex: 0}))
  );

  const {currentData, isLoading, isError, isFetching, refetch} =
    useGetCustomComplianceStandardListV2Query(
      {
        start,
        length,
        filters: scopedRequest.filters,
        filterFns: scopedRequest.filterFns,
        sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = currentData?.data ?? [];
  const totalCount = currentData?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {hasCreatePermission && onCreate && (
              <TableToolbar.Action variant="create" onClick={onCreate}>
                New Standard
              </TableToolbar.Action>
            )}
            {hasImportPermission && onImport && (
              <TableToolbar.Action variant="import" onClick={onImport} />
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
